import type { GeneratedRecurrence, InstanceDetail, InstanceReset } from "@keydials/api-client";

/**
 * The pure half of the Overview's resets line (KEY-RESETTIME): what each reset is called, which one runs
 * next, how its time reads in the dial's own zone, and the timing a dial admin's edit turns into. Kept apart
 * from the component so the parts worth testing can be tested without a DOM.
 */

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;

/** What a reset resets, in the dial's own words: "Meals · This week's meals", or the item's name when it
 * resets one item. Falls back to the keys when the structure no longer has the node. */
export function resetName(detail: Pick<InstanceDetail, "sections">, reset: Pick<InstanceReset, "sectionKey" | "segmentKey" | "itemKey">): string {
  const section = detail.sections.find((s) => s.key === reset.sectionKey);
  const segment = section?.segments.find((s) => s.key === reset.segmentKey);
  if (reset.itemKey) {
    const item = segment?.items.find((i) => i.key === reset.itemKey);
    if (item) return item.name;
  }
  if (segment) return section && section.label !== segment.name ? `${section.label} · ${segment.name}` : segment.name;
  return [reset.sectionKey, reset.segmentKey, reset.itemKey].filter(Boolean).join(" / ");
}

/** The labels of the fields a reset empties, in the dial's own words ("Meal plan"), for "empties Meal plan". */
export function clearedFieldLabels(detail: Pick<InstanceDetail, "nodeAttributes">, keys: string[]): string[] {
  return keys.map((key) => detail.nodeAttributes?.find((a) => a.key === key)?.label || key);
}

/** Soonest first; resets whose next run can't be worked out go last, in the server's order. */
export function orderResets<T extends Pick<InstanceReset, "nextResetUtc">>(resets: T[]): T[] {
  const at = (r: T) => (r.nextResetUtc ? Date.parse(r.nextResetUtc) : Number.POSITIVE_INFINITY);
  return resets.map((r, i) => ({ r, i })).sort((a, b) => at(a.r) - at(b.r) || a.i - b.i).map((x) => x.r);
}

/** "Mon 5 Oct, 04:00" in the dial's zone — the zone its reset times are written in, not the viewer's. */
export function formatResetTime(iso: string, timeZone?: string | null): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const zone = timeZone || "UTC";
  try {
    const day = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: zone }).format(date);
    const time = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: zone }).format(date);
    return `${day}, ${time}`;
  } catch {
    return date.toISOString().slice(0, 16).replace("T", " ");
  }
}

/** What a dial admin can change on a reset: the days (weekly), the day of the month (monthly) and the time. */
export interface ResetTimingDraft {
  days: string[];
  dayOfMonth: number;
  time: string;
}

export function draftFrom(recurrence: GeneratedRecurrence): ResetTimingDraft {
  return {
    days: recurrence.onDaysOfWeek?.length ? [...recurrence.onDaysOfWeek] : ["Monday"],
    dayOfMonth: recurrence.onDayOfMonth ?? 1,
    time: recurrence.atTimeOfDay ?? "00:00",
  };
}

const TIME = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

/** Why a draft can't be saved, or null when it can. Mirrors the server's rules so the button says so first. */
export function draftProblem(unit: GeneratedRecurrence["unit"], draft: ResetTimingDraft): string | null {
  if (!TIME.test(draft.time)) return "Pick a time.";
  if (unit === "Week" && draft.days.length === 0) return "Pick at least one day.";
  if (unit === "Month" && !(Number.isInteger(draft.dayOfMonth) && draft.dayOfMonth >= 1 && draft.dayOfMonth <= 31)) {
    return "Pick a day from 1 to 31.";
  }
  return null;
}

/**
 * The recurrence a draft becomes: the TEMPLATE's step and unit — which a dial can't change — with the dial's
 * day and time. Built from the template rather than the current timing so a stale value can never carry over.
 */
export function recurrenceFromDraft(template: GeneratedRecurrence, draft: ResetTimingDraft): GeneratedRecurrence {
  return {
    every: template.every,
    unit: template.unit,
    atTimeOfDay: draft.time,
    onDaysOfWeek: template.unit === "Week" ? WEEKDAYS.filter((d) => draft.days.includes(d)) : null,
    onDayOfMonth: template.unit === "Month" ? draft.dayOfMonth : null,
  };
}
